import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    const url = config.url || '';

    const isPublicAuth =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/request-password-reset') ||
      url.includes('/auth/reset-password') ||
      url.includes('/auth/reset-password/validate');

    if (token && !isPublicAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (
      error.code === 'ECONNABORTED' ||
      String(error.message || '').includes('timeout') ||
      String(error.message || '').includes('Network Error')
    ) {
      const customError = new Error('Backend service is currently unavailable');
      customError.status = 503;
      customError.isNetworkError = true;
      throw customError;
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const errorData = error.response?.data;
      const isTokenExpired = errorData?.errorCode === 'TOKEN_EXPIRED';

      if (url.includes('/auth/login') || url.includes('/auth/register')) {
        // Let login/register page handle it.
      } else if (isPublicEndpoint(url)) {
        if (isTokenExpired) {
          handleTokenExpiration();
        } else {
          console.warn('401 error on public endpoint:', url);
        }
      } else {
        handleTokenExpiration();
      }
    }

    if (error.response?.status === 403) {
      const errorData = error.response?.data;

      const permissionError = new Error(
        errorData?.message || 'You do not have permission to perform this action'
      );
      permissionError.status = 403;
      permissionError.errorCode = errorData?.errorCode;
      permissionError.isPermissionError = true;
      permissionError.requiredRole = errorData?.fieldErrors?.requiredRole;
      permissionError.currentRole = errorData?.fieldErrors?.currentRole;
      permissionError.requiredPermission = errorData?.fieldErrors?.requiredPermission;
      permissionError.originalError = errorData;

      if (window.showPermissionError) {
        window.showPermissionError(permissionError);
      } else if (window.showNotification) {
        window.showNotification(permissionError.message, 'error');
      }

      throw permissionError;
    }

    const errorData = error.response?.data;
    if (errorData?.message) {
      const customError = new Error(errorData.message);
      customError.status = error.response?.status;
      customError.errorCode = errorData.errorCode;
      customError.originalError = errorData;
      throw customError;
    }

    if (typeof error.response?.data === 'string') {
      const customError = new Error(error.response.data);
      customError.status = error.response?.status;
      throw customError;
    }

    throw error.response?.data || error;
  }
);

function isPublicEndpoint(url) {
  const publicPaths = [
    '/events',
    '/venues',
    '/seat-map',
    '/countries',
    '/cities',
    '/checkout/validate',
    '/checkout/prepare',
    '/public',
    '/auth/request-password-reset',
    '/auth/reset-password',
    '/auth/reset-password/validate',
  ];

  return publicPaths.some((path) => url.includes(path));
}

function handleTokenExpiration() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  sessionStorage.clear();

  try {
    import('../store/authStore')
      .then(({ useAuthStore }) => {
        useAuthStore.getState().logout(true);
      })
      .catch((importError) => {
        console.warn('Could not access auth store for logout:', importError);
      });
  } catch (importError) {
    console.warn('Could not dynamically import auth store:', importError);
  }

  const currentPath = window.location.pathname;
  const isOnLoginPage = currentPath === '/login' || currentPath === '/register';

  if (!isOnLoginPage) {
    window.location.href = '/login?expired=true';
  }
}

export default api;

